import React, { useEffect, useState } from "react"
import {
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ErrorMessage, Form, Formik } from "formik"
import * as Yup from "yup"
import CustomLabel from "components/Common/CustomLabel"
import { trialOperationCaptureAbsent } from "store/actions/trialPeriodAction"
import { showAlert } from "store/reducers/alertReducer"
import { formatDateForAPI } from "../../../components/Common/Utils"
import { captureRandomAbsent } from "store/actions/roasterAction"

const initialValues = {
  shift: null,
  stations: [],
  date: null,
}

const AbsentJobModal = ({ isOpen, toggle, trialOperationDetails }) => {
  const [hovered, setHovered] = useState(null)
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { work_shifts, stations } = useSelector(state => state.Common)
  const dispatch = useDispatch()
  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (trialOperationDetails) {
      setFormValues({
        shift: trialOperationDetails?.shift,
        stations: [trialOperationDetails?.station.toString()],
        date: null,
      })
    }
  }, [trialOperationDetails])

  const validationSchema = Yup.object().shape({
    shift: Yup.string().required(t("Field is required")),
    stations: Yup.array().required(t("Field is required")),
    date: Yup.date().required(),
  })

  const handleOnSubmit = values => {
    const { shift, stations, date } = values

    const payLoad = {
      shift,
      stations,
      date: formatDateForAPI(date),
    }
    dispatch(captureRandomAbsent(payLoad))
      .then(action => {
        if (!action.payload) {
          return dispatch(
            showAlert({
              text: t(" invalid."),
              type: "error",
            })
          )
        }
        const { statusCode, success, message } = action.payload

        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          toggle()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(err => {
        console.log("err", err)
      })
  }

  const buttonStyle = (selected, text, isHovered) => {
    return {
      flex: "1 1 100px",
      margin: "4px",
      padding: "10px 15px",
      border: `1px solid  ${
        selected === text || isHovered
          ? "#00778a"
          : text === "present"
          ? "green"
          : text === "absent"
          ? "red"
          : "#99c1cd"
      }`,
      background: selected === text || isHovered ? "#00778a" : "white",
      color:
        selected === text || isHovered
          ? "white"
          : text === "absent"
          ? "red"
          : text === "present"
          ? "green"
          : "#5d98a0",
      fontSize: "14px",
      cursor: "pointer",
      fontWeight: selected === text ? "bold" : "normal",
      transition: "all 0.3s ease",
      borderRadius: "5px",
      minWidth: "120px",
    }
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <Formik
        initialValues={formValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldTouched,
          setFieldValue,
          isValid,
          dirty,
        }) => (
          <Form>
            <>
              <ModalHeader toggle={toggle}>{t("Capture Absent")}</ModalHeader>
              <ModalBody>
                <Row>
                  <Col xs={12} className="mb-2">
                    <div className="form_field">
                      <CustomLabel label={t("Shift")} isRequired />
                      <div className="d-flex flex-wrap">
                        {Object.keys(work_shifts).map(option => (
                          <button
                            key={option}
                            name="shift"
                            onMouseEnter={() => setHovered(option)}
                            onMouseLeave={() => setHovered(null)}
                            onBlur={handleBlur}
                            style={buttonStyle(
                              values.shift,
                              option,
                              hovered === option
                            )}
                            onClick={() => {
                              if (trialOperationDetails?.shift) {
                                return
                              }
                              setFieldValue("shift", option)
                              // setFieldTouched("shift", true)
                            }}
                            type="button"
                          >
                            {t(
                              work_shifts[option][
                                language === "en" ? "name" : "name_ar"
                              ]
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    <ErrorMessage
                      name={"shift"}
                      component="div"
                      className="text-danger"
                    />
                  </Col>

                  <Col xs={12} className="mb-2">
                    <div className="form_field">
                      <CustomLabel label={t("Select the station")} isRequired />
                      <div className="d-flex flex-wrap">
                        {Object.keys(stations).map(option => (
                          <button
                            key={option}
                            name="station"
                            onBlur={handleBlur}
                            onMouseEnter={() => setHovered(option)}
                            onMouseLeave={() => setHovered(null)}
                            style={buttonStyle(
                              values.stations.includes(option),
                              option,
                              values.stations.includes(option) ||
                                hovered === option
                            )}
                            onClick={() => {
                              if (trialOperationDetails?.station) {
                                return
                              }
                              const currentStations = values.stations || []
                              if (currentStations.includes(option)) {
                                // Remove station from array
                                setFieldValue(
                                  "stations",
                                  currentStations.filter(
                                    station => station !== option
                                  )
                                )
                              } else {
                                // Add station to array
                                setFieldValue("stations", [
                                  ...currentStations,
                                  option,
                                ])
                              }
                              setFieldTouched("stations", true)
                            }}
                            type="button"
                          >
                            {t(
                              stations[option][
                                language === "en" ? "name" : "name_ar"
                              ]
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    <ErrorMessage
                      touched={touched}
                      name={"stations"}
                      component="div"
                      className="text-danger"
                    />
                  </Col>

                  <Col xs="12" sm="6" className="mb-1">
                    <div className="form_field">
                      <CustomLabel label={t("Date")} isRequired />
                      <Flatpickr
                        options={{
                          dateFormat: "d-m-Y",
                        }}
                        value={values.date ? new Date(values.date) : null}
                        onChange={selectedDates => {
                          setFieldValue("date", selectedDates[0])
                          // setFieldTouched("date", true)
                        }}
                        className="form-control"
                      />
                    </div>
                    <ErrorMessage
                      touched={touched}
                      name={"date"}
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="main_btn disabled-button fit_content px-4"
                    onClick={toggle}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="main_btn fit_content px-4"
                    disabled={!isValid}
                  >
                    {t("Save")}
                  </button>
                </div>
              </ModalFooter>
            </>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default AbsentJobModal
